// src/components/enterprise/ReadinessConsole.jsx
//
// Readiness & Strain: the capacity score (ERI), what it is made of, and strain
// by group with the reasoning on screen. Live mode reads the snapshot and the
// early-warning layer; the edition's modeled example stands before an
// organization has five respondents.
//
//   ERI = 0.50 performance + 0.35 wellbeing + 0.15 cognitive (0.59 / 0.41 with
//   no cognitive test; performance alone when no screener was answered).
//   Wellbeing = 100 - the worst screener's penalty - half the mean of the rest
//   (one screener at moderate costs 42: wellbeing 58, under the 60 line).
//   Quadrants: performance >= 60 is high, wellbeing >= 60 is healthy; high and
//   under together is Running Hot, low and under is Needs Support.
//   (functions/shared/scoringCore.js is canonical; docs/EXECUTIVE-METRICS.md.)
//
// Live mode (2026-11-04): the score card shows the parts of the ERI with their
// four-week change; each signal reads against a mark (the 25% population
// reference for screening positive, the 60 line for wellbeing) or against four
// weeks ago, not a hard-coded threshold; and "Strain by sector: what it is made
// of" replaces the pressure bars (100 - ERI, with bands calibrated for the
// modeled example that read Steady on live data) with each group's ERI and its
// change, performance beside wellbeing so the Running Hot shape is visible, the
// driver mix, the direction reading and the next step.

import React from 'react';
import { TrendingUp, ShieldCheck, Info, Lock } from 'lucide-react';
import { useEdition } from '../../data/editions/EditionContext';
import { useOrganization } from '../../context/OrganizationContext';
import { trendDelta } from '../../utils/executiveMetrics';
import { earlyWarningRows, hasDirection, screenShare, pts, POPULATION_LINE, DRIVER_LABELS } from '../../utils/earlyWarning';

const MIN_GROUP_SIZE = 5; // reporting floor (10 recommended for larger employers)

/* --- discipline atoms (consolidate into the shared layer later) --- */
const ConfidenceTag = ({ level }) => {
    const map = {
        Leading: 'text-emerald-700 bg-emerald-50 border-emerald-100',
        Intermediate: 'text-[var(--brand-deep)] bg-blue-50 border-blue-100',
        Lagging: 'text-amber-700 bg-amber-50 border-amber-100'
    };
    return (
        <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${map[level]}`}>
            {level}
        </span>
    );
};

const SourceTag = ({ modeled }) => (
    <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${
        modeled ? 'text-amber-700 bg-amber-50 border-amber-100' : 'text-[var(--brand-deep)] bg-blue-50 border-blue-100'
    }`}>
        {modeled ? 'Modeled' : 'Observed'}
    </span>
);

/* Support-priority band for the modeled example — a prioritisation cue, not a risk verdict. */
const bandFor = (pressure) => {
    if (pressure >= 78) return { label: 'Priority', cls: 'text-amber-700 bg-amber-50 border-amber-200' };
    if (pressure >= 70) return { label: 'Elevated', cls: 'text-[var(--brand-deep)] bg-blue-50 border-blue-100' };
    return { label: 'Steady', cls: 'text-emerald-700 bg-emerald-50 border-emerald-100' };
};

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const pctOf = (v) => (num(v) === null ? '—' : `${Math.round(v * 100)}%`);
const change = (now, prior) => (num(now) === null || num(prior) === null ? null : Math.round(now - prior));

// A change against four weeks ago: "+4" in rose when that is the wrong way,
// emerald when it is the right way, "level" at zero, nothing without a prior.
const DeltaChip = ({ value, goodWhen = 'down', suffix = '' }) => {
    if (value === null || value === undefined) return null;
    if (value === 0) return <span className="text-[10px] text-slate-400">level vs 4 wks ago</span>;
    const good = goodWhen === 'down' ? value < 0 : value > 0;
    return <span className={`text-[10px] font-semibold ${good ? 'text-emerald-700' : 'text-rose-600'}`}>{value > 0 ? '+' : '−'}{Math.abs(value)}{suffix} vs 4 wks ago</span>;
};

const PACE_TONE = {
    above: 'text-rose-700 bg-rose-50 border-rose-100',
    rising: 'text-amber-700 bg-amber-50 border-amber-100',
    steady: 'text-slate-600 bg-slate-50 border-slate-200',
    easing: 'text-emerald-700 bg-emerald-50 border-emerald-100',
    unknown: 'text-slate-500 bg-slate-50 border-slate-200'
};

const Bar = ({ label, value, color }) => (
    <div className="flex items-center gap-2">
        <span className="w-[4.5rem] text-[10px] text-slate-500 shrink-0">{label}</span>
        <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden min-w-[4rem]">
            <div className="h-2 rounded" style={{ width: `${Math.max(0, Math.min(100, num(value) || 0))}%`, backgroundColor: color }} />
        </div>
        <span className="w-6 text-right text-[11px] font-semibold tabular-nums text-slate-700">{num(value) === null ? '—' : Math.round(value)}</span>
    </div>
);

const ReadinessConsole = ({ live, metrics }) => {
    const edition = useEdition();
    const { orgData } = useOrganization();
    const adultOrg = !orgData || orgData.youthSurface !== 'athlete';
    const isLive = live?.source === 'live';
    const liveBiz = (live && live.business) || {};
    const prior = isLive ? live.prior || null : null;
    const q = isLive ? live.quadrants || {} : {};
    const screenNow = isLive ? screenShare({ needsSupportShare: num(q.needs_support), runningHotShare: num(q.running_hot) }) : null;
    const screenPrior = prior ? screenShare(prior) : null;

    // --- the signals, each against a mark or against four weeks ago ---
    const SIGNALS = isLive ? [
        { label: 'Screening positive (a validated screener at moderate or worse)', tier: 'Leading', value: pctOf(screenNow),
          delta: screenNow !== null && screenPrior !== null ? pts(screenNow) - pts(screenPrior) : null, goodWhen: 'down', suffix: ' pts',
          mark: adultOrg ? `${pts(POPULATION_LINE)}% of Canadian adults, the population reference` : null,
          tone: screenNow === null ? 'slate' : (adultOrg && screenNow > POPULATION_LINE) ? 'amber' : 'emerald' },
        { label: 'Needs Support share (both dimensions under strain)', tier: 'Leading', value: pctOf(q.needs_support),
          delta: prior ? change(pts(num(q.needs_support)), pts(num(prior.needsSupportShare))) : null, goodWhen: 'down', suffix: ' pts',
          mark: prior ? 'its own level four weeks ago' : null,
          tone: (() => { const d = prior ? change(pts(num(q.needs_support)), pts(num(prior.needsSupportShare))) : null; return d === null ? 'slate' : d >= 3 ? 'amber' : 'emerald'; })() },
        { label: 'Running Hot share (output high, wellbeing under)', tier: 'Leading', value: pctOf(q.running_hot),
          delta: prior ? change(pts(num(q.running_hot)), pts(num(prior.runningHotShare))) : null, goodWhen: 'down', suffix: ' pts',
          mark: prior ? 'its own level four weeks ago' : null,
          tone: (() => { const d = prior ? change(pts(num(q.running_hot)), pts(num(prior.runningHotShare))) : null; return d === null ? 'slate' : d >= 3 ? 'amber' : 'emerald'; })() },
        { label: 'Wellbeing (cohort mean, 0 to 100)', tier: 'Leading', value: num(live.eri?.wellbeing) === null ? '—' : `${Math.round(live.eri.wellbeing)}`,
          delta: prior ? change(num(live.eri?.wellbeing), num(prior.wellbeing)) : null, goodWhen: 'up', suffix: '',
          mark: 'the 60 line: one screener at moderate puts a person at 58',
          tone: num(live.eri?.wellbeing) === null ? 'slate' : live.eri.wellbeing < 60 ? 'amber' : 'emerald' },
        { label: 'Divergence (performance and wellbeing on opposite sides of their lines)', tier: 'Leading', value: pctOf(live.divergenceRate),
          delta: null, mark: 'the Running Hot shape in either direction; no mark', tone: 'slate' },
        ...(liveBiz.absence_days_per_fte ? [{ label: 'Absence days / FTE (uploaded)', tier: 'Lagging', value: `${liveBiz.absence_days_per_fte.value}`, delta: null, mark: `the business average of ${metrics?.bench?.absenceDays ?? 8.5}`, tone: liveBiz.absence_days_per_fte.value > (metrics?.bench?.absenceDays ?? 8.5) ? 'amber' : 'emerald' }] : []),
        ...(liveBiz.turnover_voluntary_rate ? [{ label: 'Voluntary turnover (uploaded)', tier: 'Lagging', value: `${liveBiz.turnover_voluntary_rate.value}%`, delta: null, mark: `the business average of ${metrics?.bench?.turnoverRate ?? 16}%`, tone: liveBiz.turnover_voluntary_rate.value > (metrics?.bench?.turnoverRate ?? 16) ? 'amber' : 'emerald' }] : [])
    ] : edition.readiness.signals;

    const score = (isLive && live.capacityScore != null) ? live.capacityScore : edition.summary.capacityScore;
    const trend = isLive ? trendDelta(live.capacityTrend) : edition.summary.capacityTrend;
    const scoreBasis = isLive
        ? (live.capacityBasis === 'eri'
            ? `ERI: 50% performance, 35% wellbeing, 15% cognitive (59 / 41 without the cognitive test) · ${live.eri?.nScored ?? 0} scored respondents, each at their latest check-in`
            : 'Performance mean, until wellbeing screeners reach the warehouse with the next assessments')
        : 'Modeled example';
    // Ring geometry: r=42 -> circumference ~264; offset shrinks as score rises.
    const ringOffset = Math.round(264 * (1 - score / 100));

    // The parts of the ERI, organization-wide, with their four-week change.
    const parts = isLive && live.capacityBasis === 'eri' ? [
        { k: 'Performance', w: '50%', v: num(live.eri?.perf), d: prior ? change(num(live.eri?.perf), num(prior.perf)) : null, note: 'the self-rated capacities' },
        { k: 'Wellbeing', w: '35%', v: num(live.eri?.wellbeing), d: prior ? change(num(live.eri?.wellbeing), num(prior.wellbeing)) : null, note: 'from the validated screeners' },
        { k: 'ERI', w: '', v: num(live.eri?.score), d: prior ? change(num(live.eri?.score), num(prior.eri)) : null, note: 'the composite' }
    ] : [];

    // --- strain by sector: what it is made of ---
    const warning = isLive && hasDirection(live) ? Object.fromEntries(earlyWarningRows(live, edition).map((r) => [r.name, r])) : {};
    const sectors = isLive
        ? (live.groups || [])
            .filter((g) => g && !g.suppressed && num(g.eri) !== null)
            .map((g) => {
                const w = warning[g.name] || null;
                return {
                    name: g.name, responses: g.responses,
                    eri: g.eri, eriDelta: g.prior ? change(g.eri, num(g.prior.eri)) : null,
                    perf: num(g.perf), wellbeing: num(g.wellbeing),
                    ns: num(g.needsSupportShare), rh: num(g.runningHotShare), screen: screenShare(g),
                    screenDelta: w ? w.momentum.deltaPts : null,
                    drivers: ['anxiety', 'depression', 'sleep', 'focus', 'trauma']
                        .map((key) => ({ key, label: DRIVER_LABELS[key], share: num(g.drivers && g.drivers[key]) }))
                        .filter((d) => d.share !== null && d.share >= 0.05)
                        .sort((a, b) => b.share - a.share),
                    leading: w ? w.driver : null, pace: w ? w.pace : null, nextStep: w ? w.nextStep : null
                };
            })
            .sort((a, b) => ((b.screen ?? -1) - (a.screen ?? -1)) || (a.eri - b.eri))
        : [];
    const unscored = isLive ? (live.groups || []).filter((g) => g && !g.suppressed && num(g.eri) === null) : [];
    const suppressed = isLive ? (live.groups || []).filter((g) => g && g.suppressed) : [];

    // The modeled example keeps its pressure bars.
    const DEMO_GROUPS = isLive ? [] : edition.readiness.groups.map((g) => ({ ...g, hidden: g.responses < MIN_GROUP_SIZE }));
    const demoVisible = DEMO_GROUPS.filter((g) => !g.hidden);
    const demoSuppressed = DEMO_GROUPS.filter((g) => g.hidden);

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 no-print">

            {/* ============================================================ */}
            {/* 1. ENTERPRISE ROLLUP — capacity score + aggregate signals    */}
            {/* ============================================================ */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Capacity score */}
                <div className="bg-white p-7 md:p-10 rounded-3xl border border-slate-200 shadow-sm relative overflow-hidden flex flex-col justify-center">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                    <div className="flex items-center gap-2 mb-1 relative z-10">
                        <h2 className="text-xl font-black text-[var(--brand-deep)]">{edition.readiness.scoreLabel}</h2>
                        <SourceTag modeled={!isLive} />
                        <ConfidenceTag level="Leading" />
                    </div>
                    <p className="text-sm text-slate-500 font-medium mb-2 relative z-10">Aggregate readiness across monitored groups (k ≥ {MIN_GROUP_SIZE})</p>
                    <p className="text-[11px] text-slate-400 font-medium mb-8 relative z-10">{scoreBasis}</p>

                    <div className="flex items-center justify-between relative z-10">
                        <div className="flex items-baseline gap-1">
                            <span className="text-7xl md:text-8xl font-black text-[var(--brand-blue)] tracking-tighter">{score}</span>
                            <span className="text-2xl md:text-3xl font-bold text-slate-300">/100</span>
                        </div>
                        <div className="relative w-32 h-32 flex flex-col items-center justify-center">
                            <svg className="absolute inset-0 w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="42" fill="transparent" stroke="#f1f5f9" strokeWidth="12" />
                                <circle cx="50" cy="50" r="42" fill="transparent" stroke={edition.palette.blue} strokeWidth="12" strokeDasharray="264" strokeDashoffset={ringOffset} strokeLinecap="round" />
                            </svg>
                            <div className="flex items-center gap-1 text-[var(--brand-blue)] font-black bg-white rounded-full px-3 py-1 shadow-md border border-blue-50 z-10 text-sm">
                                <TrendingUp size={16} strokeWidth={3} /> {trend >= 0 ? `+${trend}` : trend}
                            </div>
                        </div>
                    </div>

                    {parts.length > 0 && (
                        <div className="grid grid-cols-3 gap-3 mt-8 relative z-10">
                            {parts.map((p) => (
                                <div key={p.k} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{p.k}{p.w ? <span className="font-normal normal-case"> · {p.w} of the ERI</span> : null}</p>
                                    <p className="text-2xl font-bold text-[var(--brand-deep)] tabular-nums leading-tight">{p.v === null ? '—' : Math.round(p.v)}</p>
                                    <DeltaChip value={p.d} goodWhen="up" />
                                    <p className="text-[10px] text-slate-400 mt-0.5">{p.note}</p>
                                </div>
                            ))}
                        </div>
                    )}
                    <p className="text-[11px] text-slate-400 font-medium mt-6 relative z-10">{isLive ? 'The badge is the weekly ERI, first point to last, over the window. Observed, not a forecast.' : 'Directional movement vs the prior period — observed, not a forecast.'}</p>
                </div>

                {/* Aggregate signals */}
                <div className="bg-white p-7 md:p-10 rounded-3xl border border-slate-200 shadow-sm">
                    <h2 className="text-xl font-black text-[var(--brand-deep)] mb-1">Aggregate Readiness Signals</h2>
                    <p className="text-sm text-slate-500 font-medium mb-8">{isLive ? 'This organization’s observed signals, each read against a mark or against four weeks ago · aggregate only · k ≥ 5' : 'Self-reported readiness + consented operational signals · aggregate only'}</p>

                    <div className="space-y-5">
                        {SIGNALS.map((s, i) => (
                            <div key={i} className={`flex justify-between items-start gap-4 pb-4 ${i < SIGNALS.length - 1 ? 'border-b border-slate-100' : ''}`}>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-slate-600 font-semibold text-sm">{s.label}</span>
                                        <ConfidenceTag level={s.tier} />
                                    </div>
                                    {s.mark && <p className="text-[10px] text-slate-400 mt-0.5">read against {s.mark}</p>}
                                </div>
                                <div className="flex flex-col items-end shrink-0">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-2.5 h-2.5 rounded-full ${s.tone === 'amber' ? 'bg-amber-500' : s.tone === 'emerald' ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                                        <span className="font-black text-slate-800 text-base tabular-nums">{s.value}</span>
                                    </div>
                                    <DeltaChip value={s.delta} goodWhen={s.goodWhen || 'down'} suffix={s.suffix || ''} />
                                </div>
                            </div>
                        ))}
                    </div>

                    <p className="text-[11px] text-slate-400 font-light leading-relaxed mt-6 flex items-start gap-1.5">
                        <Info size={13} className="text-slate-400 shrink-0 mt-0.5" />
                        {isLive
                            ? 'Amber means over the mark, or three points or more the wrong way in four weeks. Lagging signals (absence, turnover) need an employer baseline before any causal reading. EAS surfaces signals; it makes no clinical determination.'
                            : 'Lagging signals (absenteeism, delays) need an employer baseline before any causal reading. EAS surfaces signals — it makes no clinical determination.'}
                    </p>
                </div>
            </div>

            {/* ============================================================ */}
            {/* 2. STRAIN BY SECTOR — what it is made of (live) / bars (demo) */}
            {/* ============================================================ */}
            <div className="bg-white p-7 md:p-10 rounded-3xl border border-slate-200 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-2">
                    <h2 className="text-xl font-black text-[var(--brand-deep)]">{isLive ? 'Strain by group: what it is made of' : 'Where strain is concentrated'}</h2>
                    <div className="flex items-center gap-1.5">
                        <SourceTag modeled={!isLive} />
                        <ConfidenceTag level="Leading" />
                    </div>
                </div>
                {isLive ? (
                    <>
                        <p className="text-sm text-slate-500 font-medium mb-6 max-w-4xl">
                            Each group&rsquo;s readiness (ERI) with its four-week change, performance beside wellbeing (high output over low wellbeing is the Running Hot shape), the two cohorts, the screeners at moderate or worse that make up the strain, the direction against the {pts(POPULATION_LINE)}% population mark, and the step it calls for. Groups of {MIN_GROUP_SIZE} or more, highest share screening positive first.
                        </p>
                        {sectors.length === 0 && <p className="text-sm text-slate-400">No group has five scored respondents yet.</p>}
                        {sectors.length > 0 && (
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs min-w-[900px]">
                                    <thead>
                                        <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                                            <th className="text-left font-semibold pb-2">Group</th>
                                            <th className="text-right font-semibold pb-2">Readiness</th>
                                            <th className="text-left font-semibold pb-2 pl-4">Performance · Wellbeing</th>
                                            <th className="text-right font-semibold pb-2">Needs Support · Running Hot</th>
                                            <th className="text-left font-semibold pb-2 pl-4">What it is made of</th>
                                            <th className="text-left font-semibold pb-2">Direction</th>
                                            <th className="text-left font-semibold pb-2 pl-3">Next step</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sectors.map((s) => (
                                            <tr key={s.name} className="border-t border-slate-100 align-top">
                                                <td className="py-3 pr-3 font-semibold text-slate-700">{s.name}<span className="block text-[10px] text-slate-400 font-normal">{s.responses} respondents · {s.screen === null ? '—' : `${pts(s.screen)}%`} screening positive{s.screenDelta !== null && s.screenDelta !== 0 ? ` (${s.screenDelta > 0 ? '+' : '−'}${Math.abs(s.screenDelta)} pts)` : ''}</span></td>
                                                <td className="py-3 text-right tabular-nums">
                                                    <span className="font-bold text-slate-800 text-sm">{Math.round(s.eri)}</span>
                                                    <span className="block"><DeltaChip value={s.eriDelta} goodWhen="up" /></span>
                                                </td>
                                                <td className="py-3 pl-4 w-56">
                                                    <div className="space-y-1">
                                                        {s.perf !== null && <Bar label="Performance" value={s.perf} color={edition.palette.blue} />}
                                                        <Bar label="Wellbeing" value={s.wellbeing} color={s.wellbeing !== null && s.wellbeing < 60 ? '#f59e0b' : '#10b981'} />
                                                    </div>
                                                </td>
                                                <td className="py-3 text-right tabular-nums whitespace-nowrap">
                                                    <span className="font-semibold text-rose-600">{pctOf(s.ns)}</span>
                                                    <span className="text-slate-300 mx-1">·</span>
                                                    <span className="font-semibold text-amber-600">{pctOf(s.rh)}</span>
                                                </td>
                                                <td className="py-3 pl-4 max-w-[14rem]">
                                                    {s.drivers.length === 0
                                                        ? <span className="text-slate-400">No screener at one in twenty</span>
                                                        : <div className="flex flex-wrap gap-1">{s.drivers.map((d) => (
                                                            <span key={d.key} className={`text-[10px] px-1.5 py-0.5 rounded border ${s.leading && s.leading.key === d.key ? 'font-semibold text-[var(--brand-deep)] bg-blue-50 border-blue-100' : 'text-slate-600 bg-slate-50 border-slate-200'}`}>{d.label} {pts(d.share)}%</span>
                                                        ))}</div>}
                                                </td>
                                                <td className="py-3 pr-3 max-w-[13rem]">
                                                    {s.pace
                                                        ? <><span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded border ${PACE_TONE[s.pace.kind] || PACE_TONE.unknown}`}>{s.pace.label}</span><span className="block text-[10px] text-slate-400 mt-1 leading-relaxed">{s.pace.detail}</span></>
                                                        : <span className="text-slate-400">Appears with the next snapshot</span>}
                                                </td>
                                                <td className="py-3 pl-3 text-slate-600 leading-relaxed max-w-[18rem]">{s.nextStep || <span className="text-slate-400">—</span>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {unscored.length > 0 && (
                            <p className="mt-4 text-[11px] text-slate-500">{unscored.map((g) => g.name).join(', ')}: {unscored.length === 1 ? 'reads' : 'read'} performance only until five respondents have answered a screener.</p>
                        )}
                        {suppressed.length > 0 && (
                            <div className="mt-4 flex items-center gap-2 p-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-[11px] text-slate-500 leading-relaxed">
                                <Lock size={14} className="text-slate-400 shrink-0" />
                                <span>
                                    {suppressed.map((g) => g.name).join(', ')}: {suppressed.length === 1 ? 'is' : 'are'} below the k ≥ {MIN_GROUP_SIZE} floor{suppressed.some((g) => g.suppressionReason === 'complementary') ? ' or suppressed to protect a smaller group' : ''}, not shown individually and rolled into the organization-wide figures, so no group can be worked out by subtraction.
                                </span>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <p className="text-sm text-slate-500 font-medium mb-8">
                            Normalized workload pressure (0–100, {edition.readiness.signalSubtext}) and a support-priority cue — aggregate and consented, released only at k ≥ {MIN_GROUP_SIZE}.
                        </p>
                        <div className="space-y-4">
                            {demoVisible.map((g, i) => {
                                const band = bandFor(g.pressure);
                                return (
                                    <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4 rounded-2xl border border-slate-100 bg-slate-50/60 hover:bg-slate-100/50 transition-colors">
                                        <div className="w-full sm:w-44 sm:shrink-0">
                                            <span className="block font-bold text-slate-800 text-sm">{g.name}</span>
                                            <span className="block text-xs text-slate-500 font-medium">{g.note}</span>
                                        </div>
                                        <div className="flex-1 min-w-0 flex items-center gap-3">
                                            <div className="flex-1 min-w-0 h-5 bg-slate-100 rounded-md overflow-hidden">
                                                <div className="h-full rounded-md transition-all duration-1000 ease-out" style={{ width: `${g.pressure}%`, backgroundColor: g.pressure >= 78 ? edition.palette.blue : '#38bdf8' }}></div>
                                            </div>
                                            <span className="w-7 text-right font-black text-slate-800 text-base tabular-nums shrink-0">{g.pressure}</span>
                                        </div>
                                        <span className={`shrink-0 self-start sm:self-auto text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg border ${band.cls}`}>{band.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                        {demoSuppressed.length > 0 && (
                            <div className="mt-4 flex items-center gap-2 p-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-[11px] text-slate-500 leading-relaxed">
                                <Lock size={14} className="text-slate-400 shrink-0" />
                                <span>
                                    {demoSuppressed.length} group{demoSuppressed.length > 1 ? 's' : ''} below the k ≥ {MIN_GROUP_SIZE} floor {demoSuppressed.length > 1 ? 'are' : 'is'} not shown individually and {demoSuppressed.length > 1 ? 'are' : 'is'} rolled into the enterprise aggregate, so small teams can&apos;t be re-identified.
                                </span>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ============================================================ */}
            {/* 3. DISCIPLINE FOOTER                                         */}
            {/* ============================================================ */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 flex flex-col md:flex-row gap-4 items-start">
                <ShieldCheck size={20} className="text-[var(--brand-deep)] shrink-0 mt-0.5" />
                <div className="space-y-3">
                    <p className="text-sm font-semibold text-[var(--brand-deep)]">Signals, prioritised for support — not risk verdicts.</p>
                    <div className="flex flex-wrap gap-2">
                        <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><ConfidenceTag level="Leading" /> 30–90 days · directly observable</span>
                        <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><ConfidenceTag level="Intermediate" /> 90 days–6 mo · directional</span>
                        <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><ConfidenceTag level="Lagging" /> 6–12 mo+ · needs baseline / cohort</span>
                    </div>
                    <p className="text-xs text-slate-500 font-light leading-relaxed max-w-3xl">
                        Inputs are aggregate, consented and released at k ≥ {MIN_GROUP_SIZE}. EAS does not scan message content, score individuals, or make clinical
                        determinations — it surfaces group-level signals so support can be prioritised earlier. EAS reports what it measures and labels what it models.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ReadinessConsole;
